import React, {
  useRef,
  Dispatch,
  useState,
  RefObject,
  useEffect,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import {
  Grid,
  MenuItem,
  Typography,
  FormControl,
  FormHelperText,
} from '@mui/material';

import {
  AlarmMetadata,
  AlarmDefinition,
  DataSourceAlarmMetadata,
} from 'types/types';

import StudyIntervals from './StudyIntervals';
import Select, { SelectRef } from '../../../../Common/InputComponents/Select';

import BusyHour, { BusyHourRef } from './BusyHour';
import { RulesFormValidationError } from '../CreateForm';
import { Ref as StudyIntervalsRef } from './StudyIntervals';
import { Ref as QueryBuilderRef } from '../../QueryBuilder/QueryBuilder';
import { Ref as AlarmManagementTargetCriteriaRef } from '../TargetCriteria/TargetCriteria';

interface RulesElementsProps {
  alarmMetaData: AlarmMetadata | undefined;
  alarmDefinitionData: AlarmDefinition | null;
  getDimAttributesForAggregationLevel: (
    aggregationLevelsForDimAttributes?: string[],
    resetRightData?: boolean
  ) => void;
  setAggregationLevelsForDimAttributes: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
  dataSourceMetadataForAlarm: DataSourceAlarmMetadata | undefined;
  queryBuilderRef: React.MutableRefObject<QueryBuilderRef | null>;
  alarmManagementTargetCriteriaRef: React.MutableRefObject<AlarmManagementTargetCriteriaRef | null>;
}
interface RulesRef {
  busyHour: RefObject<BusyHourRef | null>;
  aggregationLevel: RefObject<SelectRef | null>;
  studyIntervals: RefObject<StudyIntervalsRef | null>;
  rulesValidation: Dispatch<SetStateAction<RulesFormValidationError>>;
}

export interface RulesElementsRef {
  refs: RulesRef;
}

const RulesElements: ForwardRefRenderFunction<
  RulesElementsRef,
  RulesElementsProps
> = (
  {
    queryBuilderRef,
    alarmDefinitionData,
    dataSourceMetadataForAlarm,
    alarmManagementTargetCriteriaRef,
    getDimAttributesForAggregationLevel,
    setAggregationLevelsForDimAttributes,
  },
  ref
) => {
  const initialErrors: RulesFormValidationError = {
    aggregationLevel: '',
    queryBuilder: '',
    studyIntervals: '',
  };

  const aggregationLevelRef = useRef<SelectRef | null>(null);
  const studyIntervalsRef = useRef<StudyIntervalsRef | null>(null);
  const busyHourRef = useRef<BusyHourRef | null>(null);

  const [validationErrors, setValidationErrors] =
    useState<RulesFormValidationError>(initialErrors);

  const refs: RulesRef = {
    aggregationLevel: aggregationLevelRef,
    studyIntervals: studyIntervalsRef,
    busyHour: busyHourRef,
    rulesValidation: setValidationErrors,
  };

  const checkForHierarchyLevels = (event: string): string[] => {
    let aggregationLevelsForDimAttributes = [];
    const hierarchyKeys = Object.keys(
      dataSourceMetadataForAlarm?.hierarchies || {}
    );

    let aggregationLevelIndex;
    for (const key of hierarchyKeys) {
      const hierarchy = dataSourceMetadataForAlarm?.hierarchies[key];

      const matchingLevel = hierarchy?.HierarchyLevels.find(
        (s) => s.LevelIdAttr?.Id === event
      );
      aggregationLevelIndex = matchingLevel?.LevelIndex;
      const higherLevels = aggregationLevelIndex
        ? hierarchy?.HierarchyLevels.filter(
            (s) => s.LevelIndex >= aggregationLevelIndex
          ) ?? []
        : [];

      aggregationLevelsForDimAttributes = [
        ...aggregationLevelsForDimAttributes,
        ...higherLevels.map((s) => s.LevelIdAttr?.Id),
      ].reduce((acc, current) => {
        const existing = acc.find((level) => level === current);

        if (!existing) {
          acc.push(current);
        }
        return acc;
      }, []);
    }
    return aggregationLevelsForDimAttributes;
  };

  const handleOnChange = (event: string, field: string) => {
    switch (field.toUpperCase()) {
      case 'AGGREGATIONLEVEL': {
        queryBuilderRef.current?.handleResetQuery();
        const aggregationLevelsForDimAttributes =
          checkForHierarchyLevels(event);

        setAggregationLevelsForDimAttributes(aggregationLevelsForDimAttributes);
        getDimAttributesForAggregationLevel(
          aggregationLevelsForDimAttributes,
          true
        );

        if (validationErrors.aggregationLevel !== '')
          setValidationErrors((prev) => {
            return { ...prev, aggregationLevel: '' };
          });
        alarmManagementTargetCriteriaRef.current?.queryPreviewIconRef.current?.disableQueryPreview(
          event ?? ''
        );
        aggregationLevelRef!.current!.value = event;
        break;
      }
    }
  };

  useImperativeHandle(ref, () => ({
    refs: refs,
  }));

  useEffect(() => {
    if (alarmDefinitionData && dataSourceMetadataForAlarm) {
      const aggregationLevelsForDimAttributes = checkForHierarchyLevels(
        alarmDefinitionData.object_aggregation_level ?? ''
      );
      setAggregationLevelsForDimAttributes(aggregationLevelsForDimAttributes);
      if (alarmDefinitionData.output_serialized?.attributes) {
        getDimAttributesForAggregationLevel(aggregationLevelsForDimAttributes);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmDefinitionData, dataSourceMetadataForAlarm]);

  return (
    <React.Fragment>
      <Grid container spacing={2}>
        <Grid item xs={5}>
          <Typography className="form-element-label">
            Aggregation Level
          </Typography>
          <FormControl fullWidth error={!!validationErrors.aggregationLevel}>
            <Select
              size="small"
              className="input-elements"
              ref={aggregationLevelRef}
              disabled={
                !alarmDefinitionData?.object_aggregation_level &&
                dataSourceMetadataForAlarm === undefined
              }
              value={alarmDefinitionData?.object_aggregation_level ?? ''}
              handleOnChange={(event) =>
                handleOnChange(event ?? '', 'aggregationLevel')
              }
            >
              {dataSourceMetadataForAlarm?.hierarchyLevels?.map((s) => (
                <MenuItem key={`AggregationLevel-${s.id}`} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{validationErrors.aggregationLevel}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6} className="study-interval-container">
          <StudyIntervals
            ref={studyIntervalsRef}
            validationErrors={validationErrors}
            alarmDefinitionData={alarmDefinitionData}
          />
          <BusyHour
            alarmDefinitionData={alarmDefinitionData}
            ref={busyHourRef}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
export default forwardRef<RulesElementsRef, RulesElementsProps>(RulesElements);
